import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '@components/ui/PageLayout/PageLayout';
import styles from './PageFrame.module.css';

/**
 * How a route is framed, declared once in `routes.ts` next to its path:
 * - `flow` (default): a padded column that scrolls with the app;
 * - `fill`: full height, flush, the page owns its scrolling;
 * - `inset`: `fill`, keeping the page gutters (cards on the page colour);
 * - `bleed`: `flow` without gutters (a report's full-width header strip).
 */
export interface IRouteLayout {
  mode?: 'flow' | 'fill' | 'inset' | 'bleed';
  /** i18n key of the page title — the sidebar's name for the page. Absent where the title is the content. */
  title?: string;
  /** i18n key of the subtitle, unless the page publishes its own with `<PageSubtitle>`. */
  subtitle?: string;
}

type SlotName = 'actions' | 'badge' | 'subtitle';

interface IFrameSlots {
  targets: Record<SlotName, HTMLElement | null>;
  /** A slot component announces itself, so the header makes room for it; returns its release. */
  claim: (slot: SlotName) => () => void;
}

const FrameSlots = createContext<IFrameSlots | null>(null);

/**
 * Every route's frame (App.tsx wraps each one): the shared PageLayout, its
 * mode and title from the route table. What only the page knows — its
 * controls, a live tag, a subtitle that changes with its state — the page
 * publishes into the header with the slot components below, which portal
 * their children there and so keep the page's own React context.
 */
export function PageFrame({ layout, children }: { layout: IRouteLayout; children: ReactNode }) {
  const { t } = useTranslation();
  const [claims, setClaims] = useState<Record<SlotName, number>>({ actions: 0, badge: 0, subtitle: 0 });
  const [actions, setActions] = useState<HTMLElement | null>(null);
  const [badge, setBadge] = useState<HTMLElement | null>(null);
  const [subtitle, setSubtitle] = useState<HTMLElement | null>(null);

  const claim = useCallback((slot: SlotName) => {
    setClaims((c) => ({ ...c, [slot]: c[slot] + 1 }));
    return () => setClaims((c) => ({ ...c, [slot]: c[slot] - 1 }));
  }, []);
  const slots = useMemo<IFrameSlots>(() => ({ targets: { actions, badge, subtitle }, claim }), [actions, badge, subtitle, claim]);

  const mode = layout.mode ?? 'flow';
  const target = (slot: SlotName, ref: (el: HTMLElement | null) => void) =>
    claims[slot] > 0 ? <span ref={ref} className={styles.slot} /> : undefined;

  return (
    <FrameSlots.Provider value={slots}>
      <PageLayout
        fill={mode === 'fill' || mode === 'inset'}
        inset={mode === 'inset'}
        bleed={mode === 'bleed'}
        title={layout.title === undefined ? undefined : t(layout.title)}
        subtitle={target('subtitle', setSubtitle) ?? (layout.subtitle === undefined ? undefined : t(layout.subtitle))}
        badge={target('badge', setBadge)}
        actions={target('actions', setActions)}
      >
        {children}
      </PageLayout>
    </FrameSlots.Provider>
  );
}

/** Renders `children` in the frame's header slot; outside a frame (a page rendered alone, in a test) in place. */
function useSlot(slot: SlotName, children: ReactNode): ReactNode {
  const frame = useContext(FrameSlots);
  const claim = frame?.claim;
  useLayoutEffect(() => claim?.(slot), [claim, slot]);
  if (frame === null) return children;
  const target = frame.targets[slot];
  return target === null ? null : createPortal(children, target);
}

/** The page's own controls, on the right of its title. */
export function PageActions({ children }: { children: ReactNode }) {
  return useSlot('actions', children);
}

/** A tag beside the title (LIVE, MOCK). Hidden with the title when titles are off, unless the page has actions. */
export function PageBadge({ children }: { children: ReactNode }) {
  return useSlot('badge', children);
}

/** A subtitle that changes with the page's state, in place of the route's. */
export function PageSubtitle({ children }: { children: ReactNode }) {
  return useSlot('subtitle', children);
}
